import { Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import { isDefined } from 'src/app/@utils/is-defined';

import { Directive, ElementRef, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { IntersectionService } from '@core/services/intersection.service';
import { PlatformService } from '@core/services/platform.service';
import { ServerTokensService } from '@core/services/server-tokens.service';

@Directive({
    selector: '[appIntersectionLazyImage]',
    standalone: true
})
export class IntersectionLazyImageDirective implements OnInit, OnDestroy {
    private intersectionKey = 'intersectionImage';
    private unsubscribe = new Subject<void>();
    private imageElement: HTMLImageElement | null = null;
    private defaultIntersectionOptions = {
        threshold: 0.5
    };
    private defaultIndexThreshold = 2;

    @Input()
    private intersectionOptions?: IntersectionObserverInit;

    @Input()
    private intersectionClass: string = `kk-intersection_intersecting`;

    @Input()
    private src?: string = '';

    @Input()
    private srcset?: string = '';

    @Input()
    private index?: number;

    @Input()
    private indexLoadingThreshold: number | undefined;

    constructor(
        private element: ElementRef<HTMLImageElement>,
        private intersectionService: IntersectionService,
        private serverTokensService: ServerTokensService,
        private platformService: PlatformService
    ) {}

    @HostListener('load')
    @HostListener('error')
    @HostListener('complete')
    callback(): void {
        if (this.imageElement) {
            this.imageElement.classList.add(this.intersectionClass);
        }
    }

    ngOnInit(): void {
        if (!(this.element.nativeElement instanceof HTMLImageElement)) {
            return;
        }

        const intersectionOptions = {
            ...this.defaultIntersectionOptions,
            ...this.intersectionOptions
        };

        this.imageElement = this.element.nativeElement;

        if (isDefined(this.index) && this.index < (this.indexLoadingThreshold || this.defaultIndexThreshold)) {
            this.addSrc(this.imageElement);
            return;
        }

        this.imageElement.setAttribute(
            'loading',
            isDefined(this.index) && this.index < (this.indexLoadingThreshold || this.defaultIndexThreshold)
                ? 'eager'
                : 'lazy'
        );

        if (
            this.serverTokensService.isBot ||
            (this.platformService.isBrowser && 'loading' in document.createElement('img'))
        ) {
            this.addSrc(this.imageElement);
            return;
        }

        this.intersectionService
            .observe(this.intersectionKey, this.imageElement, intersectionOptions)
            .pipe(
                takeUntil(this.unsubscribe),
                filter(
                    (entry: IntersectionObserverEntry) => entry.target === this.imageElement && entry.isIntersecting
                ),
                take(1)
            )
            .subscribe(entry => this.addSrc(entry.target as HTMLImageElement));
    }

    ngOnDestroy(): void {
        this.unsubscribe.next();
        this.unsubscribe.complete();
        this.imageElement = null;
    }

    private addSrc(element: HTMLImageElement): void {
        if (this.src) {
            element.src = this.src;
        }

        if (this.srcset) {
            element.srcset = this.srcset;
        }
    }
}
